import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { BlogEditor } from "@/components/blog/BlogEditor";
import { Button } from "@/components/ui/button";
import { MediaPicker } from "@/components/ui/media-picker";
import { BlogCategorySelect } from "@/components/blog/BlogCategorySelect";
import { StatusBadge } from "@/components/ui/status-badge";
import { blogPostByIdQueryOptions } from "@/api-queries/blog";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { excerptForEdit } from "@/components/blog/edit-excerpt";
import {
  CaretLeft,
  CircleNotch,
  FloppyDisk,
  PaperPlaneTilt,
  Image as ImageIcon,
  Eye,
  EyeSlash,
  TextAa,
  PencilSimpleLine,
  Tag,
} from "@phosphor-icons/react";

export const Route = createFileRoute("/dashboard/therapist/blog/$postId/edit")({
  component: TherapistEditPost,
});

function TherapistEditPost() {
  const { postId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: post, isLoading, error } = useQuery(blogPostByIdQueryOptions(postId));

  const [title, setTitle] = useState("");
  const [content, setContent] = useState<object | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [category, setCategory] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [initialized, setInitialized] = useState(false);

  if (post && !initialized) {
    setTitle(post.title || "");
    setCoverImageUrl(post.coverImageUrl || "");
    setCategory(post.category || "");
    try {
      setContent(typeof post.content === "string" ? JSON.parse(post.content) : post.content);
    } catch {
      setContent(null);
    }
    setInitialized(true);
  }

  const handleContentChange = useCallback((newContent: object) => {
    setContent(newContent);
  }, []);

  const canSubmit = title.trim().length > 0 && content !== null && category !== "";

  const handleSubmit = async (status: string) => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/blog/${postId}/update`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          content: JSON.stringify(content),
          // Ručno pisan (npr. WP) excerpt se ne prepisuje; undefined ga ostavlja.
          excerpt: excerptForEdit({
            previousTitle: post?.title,
            previousExcerpt: post?.excerpt,
            previousContent: post?.content,
            title: title.trim(),
            content: content!,
          }),
          category,
          // null briše naslovnu sliku; undefined bi ostavio staru.
          coverImageUrl: coverImageUrl || null,
          status,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update post");
      }
      await queryClient.invalidateQueries({ queryKey: ["blog"] });
      await queryClient.invalidateQueries({ queryKey: ["therapist", "blog"] });
      navigate({ to: "/dashboard/therapist/blog" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Greška pri ažuriranju članka.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPublished = post?.status === "published";
  const wordCount = content ? countWords(content) : 0;
  const readingMinutes = Math.max(1, Math.ceil(wordCount / 200));

  if (isLoading) {
    return (
      <div className="relative mx-auto max-w-6xl pb-14">
        <div className="pt-1">
          <div className="h-4 w-20 animate-pulse rounded bg-muted" />
        </div>
        <div className="mt-10 h-12 w-2/3 animate-pulse rounded bg-muted" />
        <div className="mt-3 h-4 w-40 animate-pulse rounded bg-muted/60" />
        <div className="my-8 h-px bg-[oklch(90%_0.02_84)]" />
        <div className="h-96 animate-pulse rounded-xl bg-muted/20" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="relative mx-auto max-w-6xl pb-14">
        <Link
          to="/dashboard/therapist/blog"
          className="inline-flex items-center gap-1.5 pt-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" weight="bold" />
          Nazad na članke
        </Link>
        <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-[oklch(90%_0.02_84)] py-20 text-center">
          <h3
            className="text-lg font-semibold text-foreground"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Članak nije pronađen
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Članak koji tražite ne postoji ili je obrisan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mx-auto max-w-6xl pb-20">
      {/* Ambient warm wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-8 -z-10 h-64 rounded-[2.5rem] bg-[radial-gradient(ellipse_at_top,_oklch(96%_0.03_84)_0%,_transparent_60%)]"
      />

      {/* Top bar */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <Link
          to="/dashboard/therapist/blog"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <CaretLeft className="size-4" weight="bold" />
          Članci
        </Link>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:inline-flex"
            title={sidebarOpen ? "Sakrij panel" : "Prikaži panel"}
          >
            {sidebarOpen ? <EyeSlash className="size-4" /> : <Eye className="size-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? <CircleNotch className="size-3.5 animate-spin" /> : <FloppyDisk className="size-3.5" />}
            Spremi nacrt
          </Button>
          <Button
            size="sm"
            onClick={() => handleSubmit("pending")}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? <CircleNotch className="size-3.5 animate-spin" /> : <PaperPlaneTilt className="size-3.5" />}
            Pošalji na pregled
          </Button>
        </div>
      </div>

      {/* Eyebrow */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
          <PencilSimpleLine weight="duotone" className="size-3.5 text-primary" />
          Uredi članak
        </div>
        <StatusBadge status={post.status} />
      </div>

      {isPublished && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3.5">
          <p className="text-xs leading-relaxed text-amber-800">
            Ovaj članak je objavljen. Kad spremite izmjene, skida se sa sajta i ponovo je javan tek kad ga admin odobri.
          </p>
        </div>
      )}

      <div className="mt-6 flex gap-10">
        <article className="min-w-0 flex-1">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Naslov članka…"
            className="w-full border-none bg-transparent text-[34px] font-semibold leading-[1.05] tracking-tight text-foreground outline-none placeholder:text-muted-foreground/30 sm:text-[44px]"
            style={{ fontFamily: "var(--font-heading)" }}
          />

          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <TextAa weight="duotone" className="size-3.5" />
              <span className="tabular-nums">{wordCount}</span> riječi
            </span>
            <span className="text-muted-foreground/40">·</span>
            <span>~{readingMinutes} min čitanja</span>
          </div>

          <div className="my-8 h-px bg-[oklch(90%_0.02_84)]" />

          <div className="prose-canvas [&_.rounded-lg]:rounded-none [&_.rounded-lg]:border-x-0 [&_.rounded-lg]:border-t-0 [&_.ProseMirror]:min-h-[500px] [&_.ProseMirror]:px-0">
            <BlogEditor
              initialContent={initialized ? content || undefined : undefined}
              onChange={handleContentChange}
            />
          </div>
        </article>

        <aside
          className={cn(
            "hidden shrink-0 transition-all duration-300 md:block",
            sidebarOpen ? "w-72 opacity-100" : "w-0 overflow-hidden opacity-0",
          )}
        >
          <div className="sticky top-6 space-y-5">
            <SidebarSection icon={<Tag weight="duotone" className="size-3.5" />} label="Tema">
              <BlogCategorySelect value={category} onChange={setCategory} />
            </SidebarSection>

            <SidebarSection icon={<ImageIcon weight="duotone" className="size-3.5" />} label="Naslovna slika">
              <MediaPicker
                value={coverImageUrl}
                onChange={setCoverImageUrl}
                folder="blog"
                hint="JPG, PNG ili WebP · maks. 5 MB"
                previewHeight="h-44"
                hideUpload
              />
            </SidebarSection>

            {!isPublished && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5">
                <p className="text-xs leading-relaxed text-amber-800">
                  <strong>Napomena:</strong> Izmjene će biti poslane na ponovni pregled adminu.
                </p>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile sidebar */}
      <div className="mt-10 space-y-5 md:hidden">
        <div className="h-px bg-[oklch(90%_0.02_84)]" />
        <SidebarSection icon={<Tag weight="duotone" className="size-3.5" />} label="Tema">
          <BlogCategorySelect value={category} onChange={setCategory} />
        </SidebarSection>
        <SidebarSection icon={<ImageIcon weight="duotone" className="size-3.5" />} label="Naslovna slika">
          <MediaPicker
            value={coverImageUrl}
            onChange={setCoverImageUrl}
            folder="blog"
            hint="JPG, PNG ili WebP · maks. 5 MB"
            previewHeight="h-44"
            hideUpload
          />
        </SidebarSection>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSubmit("draft")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <FloppyDisk className="size-4" />
            Spremi nacrt
          </Button>
          <Button
            onClick={() => handleSubmit("pending")}
            disabled={isSubmitting || !canSubmit}
            className="flex-1"
          >
            <PaperPlaneTilt className="size-4" />
            Pošalji na pregled
          </Button>
        </div>
      </div>
    </div>
  );
}

function SidebarSection({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2.5">
      <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {icon}
        {label}
      </p>
      {children}
    </div>
  );
}

function countWords(content: object): number {
  try {
    let text = "";
    function walk(nodes: Array<{ text?: string; content?: unknown[] }>) {
      for (const node of nodes) {
        if (node.text) text += " " + node.text;
        if (Array.isArray(node.content)) walk(node.content as Array<{ text?: string; content?: unknown[] }>);
      }
    }
    const json = content as { content?: unknown[] };
    if (json.content) walk(json.content as Array<{ text?: string; content?: unknown[] }>);
    return text.trim().split(/\s+/).filter(Boolean).length;
  } catch { return 0; }
}
